import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    booleanAttribute,
    Component,
    ElementRef,
    HostListener,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    BehaviorSubject,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    switchMap,
    tap,
    timer
} from 'rxjs';
import { IconButtonComponent } from '../../button/button.component';
import { IconComponent } from '../../icon/icon.component';
import { PillComponent } from '../../pill/pill.component';
import { Menu, MenuConfig } from '../side-menu/models/menu.type';
import { SideMenuComponent } from '../side-menu/side-menu.component';
import { NavigationBarCarouselComponent } from './components/carousel/carousel.component';
import { CarouselItem } from './components/carousel/models/carousel.type';
import { AddAttributesDirective } from './directives/add-attributes/add-attributes.directive';
import { NavigationItem, Navigation } from './models/bar.type';
import { NavigationCategoryPipe } from './pipes/navigation-category/navigation-category.pipe';

@Component({
    selector: 'nw-navigation-bar',
    imports: [
        NavigationBarCarouselComponent,
        IconComponent,
        AsyncPipe,
        IconButtonComponent,
        SideMenuComponent,
        NavigationCategoryPipe,
        PillComponent,
        AddAttributesDirective,
        NgTemplateOutlet
    ],
    templateUrl: './bar.component.html',
    styleUrl: './bar.component.scss'
})
export class NavigationBarComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() homeUrl?: string;
    @Input() carouselItems?: CarouselItem[];
    @Input() navigationItems?: NavigationItem[];
    @Input({ transform: booleanAttribute }) disableRootBadge: boolean = false;
    @Input({ transform: booleanAttribute }) mobileGroupByCategory: boolean = false;

    @ViewChild('container') container!: ElementRef;
    @ViewChild('drawer') drawer!: ElementRef;
    @ViewChild('navbar') navbar!: ElementRef;

    private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
    private ngZone: NgZone = inject(NgZone);

    public selectedNavigationItem?: NavigationItem;
    public subMenuOpen: boolean = false;
    public drawerOpen: boolean = false;
    public closeSidePanel$: Observable<boolean> = this.breakpointObserver
        .observe('(min-width: 1200px)')
        .pipe(
            map(({ matches }) => !matches),
            filter((matches) => matches && this.subMenuOpen),
            tap(() => (this.subMenuOpen = false))
        );
    public rotate$: Observable<boolean> = this.breakpointObserver
        .observe(['(orientation: portrait)', '(orientation: landscape)'])
        .pipe(
            map(() => true),
            tap(() => {
                this.drawerOpen = false;
                this.drawerOpenSubject$.next(false);
            })
        );
    public closeDrawer$: Observable<boolean> = this.breakpointObserver
        .observe('(min-width: 1200px)')
        .pipe(
            map(({ matches }) => !matches),
            filter((matches) => !matches),
            tap(() => (this.drawerOpen = false))
        );
    public subMenuMaxWidth!: number;
    public sideMenuConfig!: MenuConfig;
    public hasRightMenuItem: boolean = false;
    // A signal so the ResizeObserver's update re-renders without zone.js.
    public spacerHeight = signal(0);
    private resizeObserver!: ResizeObserver;
    public showBadge: boolean = false;
    private subMenuOpenSubject$ = new BehaviorSubject<boolean>(false);
    public debouncedSubMenuOpen$ = this.subMenuOpenSubject$.pipe(
        distinctUntilChanged(),
        switchMap((value) =>
            value ? of(true) : timer(250).pipe(switchMap(() => of(false)))
        )
    );
    public drawerOpenSubject$ = new BehaviorSubject<boolean>(false);
    public debouncedDrawerOpen$ = this.drawerOpenSubject$.pipe(
        distinctUntilChanged(),
        switchMap((value) =>
            value ? of(true) : timer(250).pipe(switchMap(() => of(false)))
        )
    );

    @HostListener('document:click', ['$event']) onClick($event: Event) {
        if (!($event.target as Element)?.closest('.nw-navigation-bar__container')) {
            this.subMenuOpen = false;
            this.subMenuOpenSubject$.next(false);
        }
    }

    @HostListener('window:resize') onResize() {
        this.subMenuMaxWidth = this.container.nativeElement.clientWidth - 24;

        this.ngZone.runOutsideAngular(() => {
            document.documentElement.style.setProperty(
                '--app-height',
                `${window.innerHeight}px`
            );
        });
    }

    public ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            document.documentElement.style.setProperty(
                '--app-height',
                `${window.innerHeight}px`
            );
        });

        this.resizeObserver = new ResizeObserver(() =>
            this.spacerHeight.set((this.navbar?.nativeElement.clientHeight || 1) - 2)
        );

        this.resizeObserver.observe(this.navbar.nativeElement);
    }

    public ngOnDestroy(): void {
        this.resizeObserver.disconnect();
    }

    private getActiveState(navigationItem: NavigationItem): boolean {
        if (navigationItem.active) return true;
        if (navigationItem.children?.find((child) => child.active === true)) return true;

        return false;
    }

    /**
     * A leaf of the drawer. A path makes it a link, so the navigation is the host's and the
     * browser's; an onClick is the host's own handler and closes the drawer behind it.
     */
    private toSideMenuLeaf(item: Navigation.Item): Menu.ItemConfig {
        const onClick = item.onClick;

        return {
            label: item.label,
            badge: item.badge,
            active: item.active || false,
            path: item.path,
            onClick:
                onClick &&
                (() => {
                    onClick();
                    this.setDrawerOpen(false);
                }),
            attributes: item.attributes
        };
    }

    private getSideMenuChildren(
        navigationItem: NavigationItem
    ): Menu.ItemConfig[] | undefined {
        if (!navigationItem.children || navigationItem.children.length === 0)
            return undefined;

        const CHILDREN: Menu.ItemConfig[] = [];

        if (this.mobileGroupByCategory) {
            const grouped = new Map<string | undefined, Navigation.Item[]>();
            for (const child of navigationItem.children) {
                const key =
                    child.category && child.category !== 'undefined'
                        ? child.category
                        : undefined;
                if (!grouped.has(key)) grouped.set(key, []);
                grouped.get(key)!.push(child);
            }

            for (const [category, items] of grouped) {
                if (category) {
                    CHILDREN.push({
                        label: category,
                        active: items.some((item) => item.active || false),
                        attributes: {
                            'data-test': `nav-mobile-category-${category.toLowerCase().replace(/\s+/g, '-')}`
                        },
                        items: items.map((item) => this.toSideMenuLeaf(item))
                    });
                } else {
                    for (const item of items) {
                        CHILDREN.push(this.toSideMenuLeaf(item));
                    }
                }
            }
        } else {
            for (const child of navigationItem.children) {
                CHILDREN.push(this.toSideMenuLeaf(child));
            }
        }

        if (navigationItem.path) {
            CHILDREN.unshift({
                label: `View ${navigationItem?.label?.toLowerCase()}`,
                active: navigationItem.active || false,
                path: navigationItem.path
            });
        }

        return CHILDREN;
    }

    public ngOnChanges({ navigationItems, mobileGroupByCategory }: SimpleChanges): void {
        if (navigationItems?.currentValue || mobileGroupByCategory) {
            this.sideMenuConfig = {
                items: (this.navigationItems || []).map((item) => {
                    const ITEMS = this.getSideMenuChildren(item);
                    // An item with children expands rather than going anywhere itself.
                    const LEAF = ITEMS?.length ? undefined : this.toSideMenuLeaf(item);
                    return {
                        label: item.label,
                        badge: item.badge,
                        path: LEAF?.path,
                        onClick: LEAF?.onClick,
                        active: this.getActiveState(item),
                        items: ITEMS,
                        attributes: item.attributes
                    };
                }),
                multi: false
            };

            this.navigationItems?.forEach((item) => {
                item.active = this.getActiveState(item);
            });
            this.hasRightMenuItem = !!this.navigationItems?.find(
                ({ float }) => float === 'right'
            );

            this.showBadge = !!this.navigationItems?.find(({ badge }) => badge?.value);
            if (!this.showBadge) {
                this.showBadge = !!this.navigationItems?.find((item: NavigationItem) =>
                    item.children?.find((child: NavigationItem) => child.badge?.value)
                );
            }
        }
    }

    public setDrawerOpen(open: boolean): void {
        this.drawerOpen = open;
        this.drawerOpenSubject$.next(open);
    }

    /**
     * The drawer's links navigate on their own, so this is what closes it behind them — including
     * the links a host projects into the `side` slot.
     */
    public onDrawerClick(event: Event): void {
        if (!(event.target as Element)?.closest?.('a[href]')) return;

        this.setDrawerOpen(false);
    }

    public toggleSubMenu(item?: NavigationItem): void {
        if (this.selectedNavigationItem === item && this.subMenuOpen === true) {
            this.subMenuOpen = false;
            this.subMenuOpenSubject$.next(false);
            return;
        }

        if (!item) return;

        if (item.onClick && (!item.children || item.children?.length === 0)) {
            this.itemClickAndClose(item);
            return;
        }

        if (!item.children?.length) return;

        this.subMenuMaxWidth = this.container.nativeElement.clientWidth - 24;
        this.subMenuOpen = true;
        this.subMenuOpenSubject$.next(true);

        if (this.selectedNavigationItem === item) return;

        this.selectedNavigationItem = item;
    }

    /**
     * Closes the submenu however the item was activated: the bar often outlives the navigation,
     * persisted across a same-document swap or never re-created in a single-page app.
     */
    public itemClickAndClose(item: NavigationItem, event?: Event): void {
        if (!item) return;

        // Take the navigation over only when the host asked for it — a plain link has to stay one.
        if (item.onClick) {
            event?.preventDefault();
            event?.stopPropagation();
            event?.stopImmediatePropagation();

            item.onClick();
        }

        this.subMenuOpen = false;
        this.subMenuOpenSubject$.next(false);
    }
}
